import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  AlertTriangle,
  ArrowUpRight,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  Inbox,
  Key,
  Loader2,
  LogOut,
  Mail,
  Phone,
  RefreshCw,
  Search,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Reuse the same storage key as AdminCareers so the HR/Sales team only logs
// in once to access either panel.
const TOKEN_KEY = "tvcabsat:admin-token";
const PAGE_SIZE = 25;

const DEPARTMENTS = [
  { value: "", label: "Todos os departamentos" },
  { value: "comercial", label: "Comercial / Parcerias" },
  { value: "suporte", label: "Assistência Técnica" },
  { value: "financas", label: "Financeiro" },
  { value: "rh", label: "Recursos Humanos" },
  { value: "outro", label: "Outros" },
];

const DEPT_COLOR = {
  comercial: "bg-blue-100 text-blue-800 border-blue-200",
  suporte: "bg-amber-100 text-amber-800 border-amber-200",
  financas: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rh: "bg-violet-100 text-violet-800 border-violet-200",
  outro: "bg-slate-100 text-slate-700 border-slate-200",
};

const readToken = () => {
  try {
    return window.localStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
};

const writeToken = (value) => {
  try {
    if (value) window.localStorage.setItem(TOKEN_KEY, value);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Storage unavailable (private mode) — token won't persist; non-fatal.
  }
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const TokenGate = ({ onValid }) => {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    setBusy(true);
    try {
      // Probe with a 1-row query to validate the token before persisting it.
      await axios.get(`${API}/admin/contacts?limit=1`, {
        headers: { "X-Admin-Token": value.trim() },
      });
      writeToken(value.trim());
      onValid(value.trim());
      toast.success("Sessão administrativa iniciada.");
    } catch (err) {
      const code = err?.response?.status;
      toast.error(code === 401 ? "Token inválido." : "Servidor indisponível.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto mt-20 max-w-md px-6">
      <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-xl">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
          <ShieldCheck className="h-6 w-6" strokeWidth={2.4} />
        </div>
        <h1 className="font-heading mt-4 text-2xl font-black tracking-tight text-slate-900">
          Painel de Contactos
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          Insira o <span className="font-bold">ADMIN_TOKEN</span> para consultar
          os pedidos de contacto recebidos.
        </p>
        <p className="mt-1 text-[11px] text-slate-500">
          Este é o token administrativo do site, definido na variável de ambiente{" "}
          <code className="rounded bg-slate-100 px-1 py-0.5 font-mono">ADMIN_TOKEN</code>.
          Não confunda com a chave do Resend ou qualquer outra API.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-3" data-testid="admin-contacts-token-form">
          <div className="relative">
            <Key
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
              strokeWidth={2.4}
            />
            <input
              type="password"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="X-Admin-Token"
              autoFocus
              data-testid="admin-contacts-token-input"
              className="w-full rounded-lg border border-slate-300 bg-white py-3 pl-10 pr-3 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <button
            type="submit"
            disabled={busy || !value.trim()}
            data-testid="admin-contacts-token-submit"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-blue-600 px-5 py-3 text-sm font-bold text-white shadow-md transition-all hover:-translate-y-0.5 hover:bg-blue-700 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Entrar
          </button>
        </form>
      </div>
    </div>
  );
};

const FilterBar = ({ filters, setFilters, onReset, onRefresh, onExport, busy, exporting }) => {
  const update = (key) => (e) =>
    setFilters((f) => ({ ...f, [key]: e.target.value, page: 0 }));
  return (
    <div className="grid grid-cols-1 gap-3 rounded-2xl border border-slate-200 bg-white p-4 md:grid-cols-6">
      <div className="relative md:col-span-2">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          value={filters.q}
          onChange={update("q")}
          placeholder="Nome / email / mensagem / assunto"
          data-testid="admin-contacts-filter-q"
          className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        />
      </div>
      <select
        value={filters.department}
        onChange={update("department")}
        data-testid="admin-contacts-filter-department"
        className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
      >
        {DEPARTMENTS.map((d) => (
          <option key={d.value} value={d.value}>
            {d.label}
          </option>
        ))}
      </select>
      <select
        value={filters.emailed}
        onChange={update("emailed")}
        data-testid="admin-contacts-filter-emailed"
        className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
      >
        <option value="">Todos (entregues + falhados)</option>
        <option value="true">Apenas entregues</option>
        <option value="false">Apenas falhados</option>
      </select>
      <input
        type="date"
        value={filters.date_from}
        onChange={update("date_from")}
        data-testid="admin-contacts-filter-date-from"
        className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
      />
      <input
        type="date"
        value={filters.date_to}
        onChange={update("date_to")}
        data-testid="admin-contacts-filter-date-to"
        className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
      />
      <div className="flex flex-wrap gap-2 md:col-span-6">
        <button
          type="button"
          onClick={onRefresh}
          disabled={busy}
          data-testid="admin-contacts-refresh"
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-xs font-bold text-white transition-all hover:bg-slate-800 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" strokeWidth={2.5} />}
          Atualizar
        </button>
        <button
          type="button"
          onClick={onExport}
          disabled={exporting || busy}
          data-testid="admin-contacts-export-csv"
          className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-xs font-bold text-white transition-all hover:bg-emerald-700 disabled:opacity-60"
        >
          {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" strokeWidth={2.5} />}
          Exportar CSV
        </button>
        <button
          type="button"
          onClick={onReset}
          data-testid="admin-contacts-reset"
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 transition-all hover:border-slate-500"
        >
          Limpar filtros
        </button>
      </div>
    </div>
  );
};

const DeptTag = ({ value, label }) => (
  <span
    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide ${
      DEPT_COLOR[value] || DEPT_COLOR.outro
    }`}
  >
    {label || value || "Outro"}
  </span>
);

const Row = ({ item, expanded, onToggle }) => (
  <>
    <tr
      onClick={onToggle}
      data-testid={`admin-contacts-row-${item.id}`}
      className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-blue-50/40"
    >
      <td className="px-4 py-3 text-[12.5px] text-slate-600 whitespace-nowrap">
        {formatDate(item.created_at)}
      </td>
      <td className="px-4 py-3">
        <DeptTag value={item.department} label={item.department_label} />
      </td>
      <td className="px-4 py-3 text-sm font-semibold text-slate-900">
        <span className="inline-flex items-center gap-1.5">
          {item.name}
          {item.source === "chatbot" && (
            <span
              data-testid="admin-contacts-source-chatbot"
              title="Lead gerado pelo Assistente"
              className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-indigo-700"
            >
              Chatbot
            </span>
          )}
        </span>
      </td>
      <td className="px-4 py-3 text-[12.5px] text-slate-700 max-w-[260px] truncate">
        {item.subject || <span className="text-slate-400">—</span>}
      </td>
      <td className="px-4 py-3 text-[12.5px] text-slate-600">
        {item.email ? (
          <a
            href={`mailto:${item.email}`}
            onClick={(e) => e.stopPropagation()}
            className="hover:text-blue-700"
          >
            {item.email}
          </a>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-[12.5px] text-slate-600 whitespace-nowrap">
        {item.phone || "—"}
      </td>
      <td className="px-4 py-3 text-center">
        {item.emailed ? (
          <CheckCircle2 className="mx-auto h-4 w-4 text-emerald-600" strokeWidth={2.5} />
        ) : (
          <XCircle className="mx-auto h-4 w-4 text-rose-400" strokeWidth={2.5} />
        )}
      </td>
    </tr>
    {expanded && (
      <tr className="bg-slate-50/60">
        <td colSpan={7} className="px-4 py-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="md:col-span-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                Mensagem
              </div>
              <p className="mt-1.5 whitespace-pre-wrap text-sm text-slate-800">
                {item.message?.trim() || <span className="text-slate-400">— (vazio)</span>}
              </p>
            </div>
            <div className="space-y-3">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                  Encaminhado para
                </div>
                <div className="mt-1.5 text-sm font-semibold text-slate-900">
                  {item.department_label || "Equipa TVCabSat"}
                </div>
                <a
                  href={`mailto:${item.department_email}`}
                  className="text-[12px] text-blue-700 hover:underline"
                >
                  {item.department_email}
                </a>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-[12px]">
                <span className="text-slate-500">Auto-reply:</span>
                {item.autoreplied ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 font-bold text-emerald-800">
                    <CheckCircle2 className="h-3 w-3" strokeWidth={2.5} /> enviado
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 font-bold text-slate-600">
                    <XCircle className="h-3 w-3" strokeWidth={2.5} /> não enviado
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 text-[12px]">
                <span className="text-slate-500">Idioma:</span>
                <span className="font-mono font-bold text-slate-700 uppercase">
                  {item.locale || "pt"}
                </span>
              </div>
              {item.email_error && (
                <div
                  className="rounded-lg border border-rose-200 bg-rose-50 px-2 py-1.5 text-[11px] text-rose-900"
                  data-testid={`admin-contacts-row-error-${item.id}`}
                >
                  <span className="font-bold uppercase tracking-wider text-rose-700">
                    Falha de envio:
                  </span>{" "}
                  <span className="font-mono">{item.email_error}</span>
                </div>
              )}
              <a
                href={`mailto:${item.email}?subject=Re:%20${encodeURIComponent(
                  item.subject || "Pedido via site",
                )}`}
                className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-blue-700 hover:underline"
              >
                Responder ao cliente <ArrowUpRight className="h-3 w-3" strokeWidth={2.5} />
              </a>
            </div>
          </div>
        </td>
      </tr>
    )}
  </>
);

export default function AdminContacts() {
  const [token, setToken] = useState(() => readToken());
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [emailHealth, setEmailHealth] = useState(null);
  const [filters, setFilters] = useState({
    q: "",
    department: "",
    emailed: "",
    date_from: "",
    date_to: "",
    page: 0,
  });

  const buildParams = useCallback(
    (limit, skip) => {
      const params = new URLSearchParams();
      params.set("limit", String(limit));
      params.set("skip", String(skip));
      if (filters.q) params.set("q", filters.q);
      if (filters.department) params.set("department", filters.department);
      if (filters.emailed !== "") params.set("emailed", filters.emailed);
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      return params;
    },
    [filters],
  );

  const fetchData = useCallback(async () => {
    if (!token) return;
    setBusy(true);
    try {
      const params = buildParams(PAGE_SIZE, filters.page * PAGE_SIZE);
      const { data } = await axios.get(
        `${API}/admin/contacts?${params.toString()}`,
        { headers: { "X-Admin-Token": token } },
      );
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 401) {
        toast.error("Sessão expirada — entre novamente.");
        writeToken("");
        setToken("");
      } else {
        toast.error("Falha ao carregar contactos.");
      }
    } finally {
      setBusy(false);
    }
  }, [token, filters.page, buildParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // One-shot probe: reports whether outbound email (Resend) is healthy in
  // the current environment. Banner stays mounted but only renders content
  // when `ok===false` to keep the UI quiet when everything works.
  useEffect(() => {
    if (!token) {
      setEmailHealth(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/email/healthcheck`, {
          headers: { "X-Admin-Token": token },
        });
        if (!cancelled) setEmailHealth(data);
      } catch {
        // Healthcheck itself failed (network/server) — leave banner hidden
        // so we don't add yet another false-positive alert on top of the
        // real "no emails ever sent" issue.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  const logout = () => {
    writeToken("");
    setToken("");
    setItems([]);
    setTotal(0);
    toast.success("Sessão terminada.");
  };

  const resetFilters = () =>
    setFilters({ q: "", department: "", emailed: "", date_from: "", date_to: "", page: 0 });

  // RFC 4180 escaping — wrap every value in double quotes and double any
  // existing quotes inside. Newlines and accents are preserved natively.
  const csvEscape = (val) => {
    const s = val == null ? "" : String(val);
    return `"${s.replace(/"/g, '""')}"`;
  };

  const exportCsv = async () => {
    if (!token) return;
    setExporting(true);
    try {
      // Fetch up to 200 (server max) matching the current filters.
      const params = buildParams(200, 0);
      const { data } = await axios.get(
        `${API}/admin/contacts?${params.toString()}`,
        { headers: { "X-Admin-Token": token } },
      );
      const rows = data.items || [];
      if (rows.length === 0) {
        toast.error("Sem contactos para exportar.");
        return;
      }
      const headers = [
        "Recebido",
        "Departamento",
        "Email destino",
        "Nome",
        "Email",
        "Telefone",
        "Assunto",
        "Mensagem",
        "Idioma",
        "Entregue",
        "Auto-reply",
      ];
      const lines = [headers.map(csvEscape).join(",")];
      rows.forEach((it) => {
        lines.push(
          [
            it.created_at,
            it.department_label || it.department || "Outro",
            it.department_email || "",
            it.name,
            it.email,
            it.phone,
            it.subject,
            it.message,
            (it.locale || "pt").toUpperCase(),
            it.emailed ? "Sim" : "Não",
            it.autoreplied ? "Sim" : "Não",
          ]
            .map(csvEscape)
            .join(","),
        );
      });
      // \uFEFF BOM keeps Excel from mangling Portuguese accents.
      const blob = new Blob([`\uFEFF${lines.join("\n")}`], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tvcabsat-contactos-${stamp}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exportados ${rows.length} contactos.`);
    } catch (err) {
      const code = err?.response?.status;
      toast.error(code === 401 ? "Sessão expirada." : "Falha ao exportar.");
    } finally {
      setExporting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <TokenGate onValid={setToken} />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <section className="pb-16 pt-28 md:pt-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.28em] text-blue-700">
                <Inbox className="h-3.5 w-3.5" strokeWidth={2.5} />
                Painel administrativo — Contactos
              </div>
              <h1 className="font-heading mt-2 text-3xl font-black tracking-tight text-slate-900 md:text-4xl">
                Pedidos de contacto recebidos
              </h1>
              <p className="mt-1 text-sm text-slate-600">
                Total: <span className="font-bold text-slate-900">{total}</span>
                <span className="ml-3 text-slate-400">·</span>
                <a
                  href="/admin/carreiras"
                  className="ml-3 inline-flex items-center gap-1 font-semibold text-blue-700 hover:underline"
                  data-testid="admin-contacts-link-careers"
                >
                  Ir para Carreiras <ArrowUpRight className="h-3 w-3" strokeWidth={2.5} />
                </a>
              </p>
            </div>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-contacts-logout"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 transition-all hover:border-rose-400 hover:text-rose-700"
            >
              <LogOut className="h-3.5 w-3.5" strokeWidth={2.4} />
              Sair
            </button>
          </div>

          <div className="mt-6">
            {emailHealth && !emailHealth.ok && (
              <div
                className="mb-4 rounded-2xl border-2 border-rose-300 bg-rose-50 p-4"
                data-testid="admin-contacts-email-health-banner"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-700">
                    <AlertTriangle className="h-4 w-4" strokeWidth={2.6} />
                  </div>
                  <div className="flex-1">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-rose-700">
                      Serviço de email · {emailHealth.status}
                    </div>
                    <h2 className="font-heading mt-1 text-base font-black text-rose-900">
                      Nenhum email está a ser enviado a partir do site.
                    </h2>
                    <p className="mt-1 text-[13px] leading-relaxed text-rose-800">
                      Os pedidos de contacto estão a ser gravados no painel, mas não chegam
                      aos departamentos nem é enviado auto-reply aos clientes.
                      {emailHealth.hint && (
                        <span className="ml-1 font-semibold">{emailHealth.hint}</span>
                      )}
                    </p>
                    {emailHealth.error && (
                      <pre className="mt-2 max-w-full overflow-x-auto rounded bg-rose-100/70 px-2 py-1 font-mono text-[11px] text-rose-900">
                        {emailHealth.error}
                      </pre>
                    )}
                    <div className="mt-2 text-[12px] text-rose-700">
                      Passos: Painel Emergent → Environment → editar{" "}
                      <code className="rounded bg-white/70 px-1 font-mono text-[11px]">
                        RESEND_API_KEY
                      </code>{" "}
                      e{" "}
                      <code className="rounded bg-white/70 px-1 font-mono text-[11px]">
                        RESEND_SENDER_EMAIL
                      </code>{" "}
                      → Redeploy. Obtenha a chave em{" "}
                      <a
                        href="https://resend.com/api-keys"
                        target="_blank"
                        rel="noreferrer"
                        className="font-bold underline hover:text-rose-900"
                      >
                        resend.com/api-keys
                      </a>
                      .
                    </div>
                  </div>
                </div>
              </div>
            )}
            {emailHealth && emailHealth.ok && (
              <div
                className="mb-4 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[11.5px] font-bold text-emerald-800"
                data-testid="admin-contacts-email-health-ok"
              >
                <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.6} />
                Serviço de email operacional · remetente {emailHealth.sender}
              </div>
            )}
            <FilterBar
              filters={filters}
              setFilters={setFilters}
              onReset={resetFilters}
              onRefresh={fetchData}
              onExport={exportCsv}
              busy={busy}
              exporting={exporting}
            />
          </div>

          <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full" data-testid="admin-contacts-table">
                <thead className="bg-slate-50">
                  <tr className="text-left font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                    <th className="px-4 py-3 inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" strokeWidth={2.5} /> Recebido
                    </th>
                    <th className="px-4 py-3">Departamento</th>
                    <th className="px-4 py-3">Cliente</th>
                    <th className="px-4 py-3">Assunto</th>
                    <th className="px-4 py-3 inline-flex items-center gap-1">
                      <Mail className="h-3 w-3" strokeWidth={2.5} /> Email
                    </th>
                    <th className="px-4 py-3 inline-flex items-center gap-1">
                      <Phone className="h-3 w-3" strokeWidth={2.5} /> Telefone
                    </th>
                    <th className="px-4 py-3 text-center">Entregue</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 && !busy && (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-500">
                        Sem contactos para os filtros atuais.
                      </td>
                    </tr>
                  )}
                  {items.map((it) => (
                    <Row
                      key={it.id}
                      item={it}
                      expanded={expanded === it.id}
                      onToggle={() => setExpanded((cur) => (cur === it.id ? null : it.id))}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="mt-5 flex items-center justify-between text-sm text-slate-600">
              <div>
                Página <span className="font-bold text-slate-900">{filters.page + 1}</span> de {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={filters.page === 0}
                  onClick={() => setFilters((f) => ({ ...f, page: Math.max(0, f.page - 1) }))}
                  data-testid="admin-contacts-page-prev"
                  className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                >
                  <ChevronLeft className="h-3.5 w-3.5" /> Anterior
                </button>
                <button
                  type="button"
                  disabled={filters.page + 1 >= totalPages}
                  onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
                  data-testid="admin-contacts-page-next"
                  className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                >
                  Próxima <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
